import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import axiosInstance from '../../../api/axiosInstance';
import * as XLSX from 'xlsx';

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // --- FILTER STATE ---
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterMethod, setFilterMethod] = useState("all");
  
  // NEW ADVANCED FILTERS
  const [filterAmount, setFilterAmount] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [dateType, setDateType] = useState("submitted_at");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // --- VIEW STATES ---
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [viewProofUrl, setViewProofUrl] = useState(null);
  const [downloadToast, setDownloadToast] = useState({ show: false, filename: '' });

  // --- NEW INSTALLMENT STATES ---
  const [remainingInstallments, setRemainingInstallments] = useState([]);
  const [selectedInstallments, setSelectedInstallments] = useState([]);
  const [isLoadingInstallments, setIsLoadingInstallments] = useState(false);

  const [isCheckingPending, setIsCheckingPending] = useState(false);
  const [showPendingAlert, setShowPendingAlert] = useState(false);

  // Auto-calculate the total amount whenever installments are selected
  useEffect(() => {
    const total = remainingInstallments
      .filter(i => selectedInstallments.includes(i.no))
      .reduce((sum, i) => sum + Number(i.total), 0);
      
    setPaymentForm(prev => ({ ...prev, amount: total ? String(total) : "" }));
  }, [selectedInstallments, remainingInstallments]);

  // --- STRICT SEQUENTIAL SELECTION LOGIC ---
  const handleToggleInstallment = (inst) => {
    // Sort by installment number to guarantee correct order regardless of API response order
    const sorted = [...remainingInstallments].sort((a, b) => a.no - b.no);

    // All overdue installment numbers
    const overdueNos = sorted.filter(i => i.status === 'overdue').map(i => i.no);

    // Block selecting any UPCOMING installment until ALL overdues are selected
    if (inst.status !== 'overdue') {
      const allOverduesSelected = overdueNos.every(no => selectedInstallments.includes(no));
      if (!allOverduesSelected) {
        alert("Please select and clear all OVERDUE installments first.");
        return;
      }
    }

    const isSelected = selectedInstallments.includes(inst.no);

    if (isSelected) {
      // Deselecting: remove this month AND every month that comes after it
      setSelectedInstallments(prev => prev.filter(no => no < inst.no));
    } else {
      // Selecting: include every installment up to and including this one (cascade upward)
      const newSelections = sorted.filter(i => i.no <= inst.no).map(i => i.no);
      setSelectedInstallments(newSelections);
    }
  };

  // --- FORM STATE ---
  const [paymentForm, setPaymentForm] = useState({
    amount: "", transaction: "", sender: "", method: "UPI", type: "installment", month: "1", proof: null, paid_at: ""
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // --- TIME WINDOW CALCULATION ---
  const getLocalDatetimeString = (dateObj) => {
    const offset = dateObj.getTimezoneOffset() * 60000;
    return (new Date(dateObj.getTime() - offset)).toISOString().slice(0, 16);
  };

  const now = new Date();
  const maxDateString = getLocalDatetimeString(now); 
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(now.getDate() - 7);
  const minDateString = getLocalDatetimeString(sevenDaysAgo); 

  // --- CUSTOM ALERT STATE ---
  const [alertConfig, setAlertConfig] = useState({ isOpen: false, title: "", message: "", type: "error" });
  const showAlert = (title, message, type = "error") => setAlertConfig({ isOpen: true, title, message, type });
  const closeAlert = () => setAlertConfig(prev => ({ ...prev, isOpen: false }));


  // --- API: FETCH PAYMENTS ---
  const fetchPayments = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get('/pay/all');
      let safeArray = [];
      if (Array.isArray(response.data)) safeArray = response.data;
      else if (response.data?.data && Array.isArray(response.data.data)) safeArray = response.data.data;
      else if (response.data?.payments && Array.isArray(response.data.payments)) safeArray = response.data.payments;
      
      setPayments(safeArray);
    } catch (err) {
      console.error("Error fetching payments:", err);
      if (err.response?.status === 500) setError("Something went wrong, please try again after some time.");
      else if (err.response?.status === 400) setError(err.response.data?.message || "Bad Request: Missing token or parameters.");
      else setError("Failed to load payments data. Please check your connection.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);
  const handleRefresh = () => fetchPayments();
  // --- ADVANCED FILTERING LOGIC ---
  const filteredPayments = payments.filter(p => {
    const safeSearch = searchTerm.toLowerCase();
    
    // 1. Basic Text Matches
    const matchesSearch = 
      (p.transaction?.toLowerCase() || "").includes(safeSearch) || 
      (p.id?.toLowerCase() || "").includes(safeSearch) || 
      (p.sender?.toLowerCase() || "").includes(safeSearch);
      
    const matchesStatus = filterStatus === "all" || (p.status || "").toLowerCase() === filterStatus.toLowerCase();
    const matchesType = filterType === "all" || (p.type || "").toLowerCase() === filterType.toLowerCase();
    const matchesMethod = filterMethod === "all" || (p.method || "").toLowerCase() === filterMethod.toLowerCase();
    
    // 2. Exact Value Matches
    const matchesAmount = filterAmount === "" || Number(p.amount) === Number(filterAmount);
    const matchesMonth = filterMonth === "" || String(p.month) === String(filterMonth);

    // 3. Date Range Match
    let matchesDate = true;
    if (fromDate || toDate) {
      const targetDateStr = p[dateType];
      if (!targetDateStr) {
        matchesDate = false; // If the payment doesn't have this date (e.g. not verified yet)
      } else {
        const targetDate = new Date(targetDateStr).setHours(0,0,0,0);
        const from = fromDate ? new Date(fromDate).setHours(0,0,0,0) : -Infinity;
        const to = toDate ? new Date(toDate).setHours(23,59,59,999) : Infinity;
        matchesDate = targetDate >= from && targetDate <= to;
      }
    }

    return matchesSearch && matchesStatus && matchesType && matchesMethod && matchesAmount && matchesMonth && matchesDate;
  });

  const hasActiveFilters = searchTerm || filterStatus !== 'all' || filterType !== 'all' || filterMethod !== 'all' || filterAmount || filterMonth || fromDate || toDate;

  const handleResetFilters = () => {
    setSearchTerm("");
    setFilterStatus("all");
    setFilterType("all");
    setFilterMethod("all");
    setFilterAmount("");
    setFilterMonth("");
    setDateType("submitted_at");
    setFromDate("");
    setToDate("");
  };

  // --- UPGRADED CROSS-OS EXPORT ---
  const handleExport = () => {
    if (filteredPayments.length === 0) {
      alert("No data available to export.");
      return;
    }

    const exportData = filteredPayments.map(p => ({
      "Payment ID": p.id,
      "Amount (₹)": Number(p.amount),
      "Transaction ID": p.transaction,
      "Sender Name": p.sender,
      "Method": p.method,
      "Type": p.type.toUpperCase(),
      "Month": p.month || "N/A",
      "Status": p.status.toUpperCase(),
      "Paid Date": p.paid_at ? new Date(p.paid_at).toLocaleString('en-IN') : "-",
      "Submitted Date": new Date(p.submitted_at).toLocaleString('en-IN'),
      "Remarks": p.remarks || "None"
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Payments");
    
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    
    // Safe Mobile/Desktop Anchor Download
    const link = document.createElement('a');
    link.style.display = 'none';
    link.href = url;
    const safeFilename = `Payment_History_${new Date().toISOString().split('T')[0]}.xlsx`;
    link.download = safeFilename;
    document.body.appendChild(link);
    link.click();
    
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setDownloadToast({ show: true, filename: safeFilename });
      setTimeout(() => setDownloadToast({ show: false, filename: '' }), 4000);
    }, 100);
  };

  // --- FORM HANDLERS ---
  const handleFormChange = (e) => {
    const { name, value } = e.target;

    // Switching payment type: reset installment selections and calculated amount
    // so stale selections from a previous type don't bleed into the new type
    if (name === 'type') {
      setSelectedInstallments([]);
      setPaymentForm(prev => ({ ...prev, [name]: value, amount: "" }));
      return;
    }

    if (name === 'paid_at' && value) {
      const selectedDate = new Date(value);
      const rightNow = new Date();
      if (selectedDate > rightNow) {
        alert("You cannot select a future time.");
        const offset = rightNow.getTimezoneOffset() * 60000;
        const currentLocalTime = new Date(rightNow.getTime() - offset).toISOString().slice(0, 16);
        setPaymentForm(prev => ({ ...prev, [name]: currentLocalTime }));
        return; 
      }
    }
    setPaymentForm(prev => ({ ...prev, [name]: value }));
  };

  // 2. Add this helper function to instantly read the ID from the local token
const getUserIdFromToken = () => {
  try {
    const token = localStorage.getItem('capstonne_access_token');
    if (!token) return null;
    
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join('')
    );
    
    const payload = JSON.parse(jsonPayload);
    // Look for standard ID fields
    return payload.id || payload.user_id || payload.userId || null;
  } catch (error) {
    console.error("Could not decode token for User ID", error);
    return null;
  }
};

// 3. The updated Click Handler
const handleRecordPaymentClick = async () => {
  setIsCheckingPending(true);
  try {
    // A. Instantly grab the ID from the local token (Zero network delay!)
    let userId = getUserIdFromToken();

    // B. BULLETPROOF FALLBACK: If token doesn't have the ID, fetch it from profile API
    if (!userId) {
      const profileRes = await axiosInstance.get('/profile/get');
      userId = profileRes.data?.id;
    }

    // C. If we STILL don't have an ID, abort safely
    if (!userId) {
      alert("Could not identify your account. Please refresh the page or log in again.");
      setIsCheckingPending(false);
      return;
    }

    // D. Call the pending API with the ID
    const response = await axiosInstance.get(`/child/pay/pending/${userId}`);
    
    if (response.data && response.data.count > 0) {
      // Block them and show the alert
      setShowPendingAlert(true);
    } else {
      // Clear to proceed!
      handleOpenCreateModal();
    }
  } catch (error) {
    console.error("Error checking pending payment status:", error);
    alert("Unable to verify payment status. Please check your connection and try again.");
  } finally {
    setIsCheckingPending(false);
  }
};
  const handleOpenCreateModal = async () => {
    setPaymentForm({ amount: "", transaction: "", sender: "", method: "UPI", type: "installment", month: "1", paid_at: "" });
    clearFile();
    setSelectedInstallments([]);
    setRemainingInstallments([]);
    setIsCreateModalOpen(true);
    
    setIsLoadingInstallments(true);
    try {
      const response = await axiosInstance.get('/installments/remaining');
      // API returns { count: number, remaining: [...] }
      const dataList = Array.isArray(response.data?.remaining) ? response.data.remaining : [];
      // Sort by installment number ascending to ensure consistent ordering
      const sorted = [...dataList].sort((a, b) => a.no - b.no);
      setRemainingInstallments(sorted);
    } catch (err) {
      console.error("Failed to load installments:", err);
    } finally {
      setIsLoadingInstallments(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert("File is too large. Please select an image under 2MB.");
      fileInputRef.current.value = "";
      return;
    }
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const clearFile = () => {
    // Revoke the object URL to free memory before clearing state
    setPreviewUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const compressImage = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const MAX_WIDTH = 1200; const MAX_HEIGHT = 1200;
          let width = img.width; let height = img.height;
          if (width > height && width > MAX_WIDTH) { height *= MAX_WIDTH / width; width = MAX_WIDTH; } 
          else if (height > MAX_HEIGHT) { width *= MAX_HEIGHT / height; height = MAX_HEIGHT; }
          canvas.width = width; canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          let quality = 0.9;
          const getBlob = () => {
            canvas.toBlob((blob) => {
              if (blob.size > 500 * 1024 && quality > 0.1) { quality -= 0.1; getBlob(); } 
              else { resolve(new File([blob], file.name, { type: 'image/jpeg' })); }
            }, 'image/jpeg', quality);
          };
          getBlob();
        };
      };
    });
  };

  // Centralised close handler — always cleans up file object URLs to prevent memory leaks
  const handleCloseCreateModal = () => {
    clearFile();
    setSelectedInstallments([]);
    setIsCreateModalOpen(false);
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();

    // Only require installment selection when the type is 'installment'
    if (paymentForm.type === 'installment' && selectedInstallments.length === 0) {
      return alert("Please select at least one installment month to pay.");
    }
    if (Number(paymentForm.amount) <= 0) return alert("Amount must be greater than 0.");
    if (paymentForm.transaction.length < 5) return alert("Transaction ID must be at least 5 characters long.");
    if (paymentForm.sender.trim().length < 2) return alert("Please enter a valid sender name.");
    if (!selectedFile) return alert("Please upload a payment proof screenshot.");
    if (!paymentForm.paid_at) return alert("Please enter the date and time of payment.");

    const exactNow = new Date();
    const exactSevenDaysAgo = new Date();
    exactSevenDaysAgo.setDate(exactNow.getDate() - 7);
    const selectedDate = new Date(paymentForm.paid_at);
    
    if (selectedDate > exactNow) return alert("Payment time cannot be in the future.");
    if (selectedDate < exactSevenDaysAgo) return alert("Payment date cannot be older than 7 days.");
    
    setIsSubmitting(true);
    try {
      const compressedFile = await compressImage(selectedFile);
      const formData = new FormData();
      formData.append('amount', paymentForm.amount);
      formData.append('transaction', paymentForm.transaction.trim());
      formData.append('sender', paymentForm.sender.trim());
      formData.append('method', paymentForm.method);
      formData.append('paid_at', new Date(paymentForm.paid_at).toISOString()); 
      formData.append('proof', compressedFile);

      if (paymentForm.type === 'installment') {
        formData.append('months', selectedInstallments.length);
      }

      await axiosInstance.post('/pay/submit', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

      setPaymentForm({ amount: "", transaction: "", sender: "", method: "UPI", type: "installment", month: "1", paid_at: "" });
      handleCloseCreateModal();
      fetchPayments(); 
    } catch (err) {
      console.error("Submission failed:", err);
      let errorMsg = "An unexpected error occurred.";
      if (err.response?.status === 500) errorMsg = "Something went wrong, please try again.";
      else if (err.response?.data) {
        const data = err.response.data;
        if (typeof data === 'string') errorMsg = data;
        else if (data.errors && Array.isArray(data.errors)) errorMsg = data.errors.join(" • "); 
        else if (data.errors && typeof data.errors === 'object') errorMsg = Object.values(data.errors).flat().join(" • "); 
        else if (data.message) errorMsg = data.message;
        else if (data.error) errorMsg = data.error;
      }
      showAlert("Submission Failed", errorMsg, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewProof = async (paymentId) => {
    if (!paymentId) return showAlert("Error", "Invalid payment ID.", "error");
    try {
      const response = await axiosInstance.get(`/pay/proof?id=${paymentId}`, { responseType: 'blob' });
      setViewProofUrl(URL.createObjectURL(response.data));
    } catch (err) {
      showAlert("Image Load Failed", "Could not retrieve the proof document.", "error");
    }
  };

  const closeProofModal = () => {
    if (viewProofUrl) URL.revokeObjectURL(viewProofUrl);
    setViewProofUrl(null);
  };

  const formatDate = (isoString) => isoString ? new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(isoString)) : "-";
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved': return <span className="px-2.5 py-1 rounded text-[11px] font-bold bg-success-bg text-success-text border border-success-border uppercase tracking-wider">Approved</span>;
      case 'pending': return <span className="px-2.5 py-1 rounded text-[11px] font-bold bg-warning-bg text-warning-text border border-warning-border uppercase tracking-wider">Pending</span>;
      case 'rejected': return <span className="px-2.5 py-1 rounded text-[11px] font-bold bg-danger-bg text-danger-text border border-danger-border uppercase tracking-wider">Rejected</span>;
      default: return <span className="px-2.5 py-1 rounded text-[11px] font-bold bg-theme-bg text-theme-muted border border-theme-border uppercase tracking-wider">{status}</span>;
    }
  };

// --- REUSABLE FILTER COMPONENT (FLEXIBLE WRAP LAYOUT) ---
const FilterInputs = () => (
  <>
    <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={`${F} w-full xl:w-auto`}>
      <option value="all">Status</option>
      <option value="approved">Approved</option>
      <option value="pending">Pending</option>
      <option value="rejected">Rejected</option>
    </select>
    
    <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className={`${F} w-full xl:w-auto`}>
      <option value="all">Types</option>
      <option value="registration">Joining</option>
      <option value="installment">Installment</option>
      <option value="full">Full</option>
    </select>
    
    <select value={filterMethod} onChange={(e) => setFilterMethod(e.target.value)} className={`${F} w-full xl:w-auto`}>
      <option value="all">Methods</option>
      <option value="upi">UPI</option>
      <option value="Bank Transfer">Bank</option>
      <option value="cash">Cash</option>
    </select>
    
    <input type="number" min="0" value={filterAmount} onChange={e => setFilterAmount(e.target.value)} placeholder="₹ Amt" className={`${F} w-full xl:w-24`} />
    
    <input type="number" min="1" max="24" value={filterMonth} onChange={e => setFilterMonth(e.target.value)} placeholder="Month" className={`${F} w-full xl:w-20`} />
    
    <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-50 dark:bg-slate-900 rounded-md border border-slate-200 dark:border-slate-700 whitespace-nowrap h-[38px] flex-shrink-0">
      <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
      <select value={dateType} onChange={e => setDateType(e.target.value)} className="bg-transparent text-xs font-semibold text-slate-500 dark:text-slate-400 outline-none cursor-pointer">
        <option value="submitted_at">Sub</option>
        <option value="paid_at">Paid</option>
        <option value="verified_at">Ver</option>
      </select>
      <span className="text-slate-300 dark:text-slate-600 select-none mx-1">|</span>
      <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="bg-transparent text-xs text-slate-600 dark:text-slate-300 outline-none cursor-pointer w-[95px] sm:w-[105px]" />
      <span className="text-[10px] font-bold text-slate-400 select-none mx-0.5">–</span>
      <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="bg-transparent text-xs text-slate-600 dark:text-slate-300 outline-none cursor-pointer w-[95px] sm:w-[105px]" />
    </div>
  </>
);
// Common styling for all filter inputs
const F = "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md py-2 px-3 text-sm font-medium outline-none text-slate-700 dark:text-slate-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all";
  return (
    <div className="flex flex-col h-[calc(100vh-80px)] bg-theme-bg p-2 sm:p-4 md:p-6 overflow-hidden relative">
     <style>{`
        @keyframes blowUp { 0% { transform: scale(0.9); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
        .animate-blowUp { animation: blowUp 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
        
        /* NEW: Slide in from right for the new toast placement */
        @keyframes slideInRight { 0% { transform: translateX(100%); opacity: 0; } 100% { transform: translateX(0); opacity: 1; } }
        .animate-slideInRight { animation: slideInRight 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        
        /* NEW: Progress bar animation (0 to 100% over 4 seconds) */
        @keyframes toastProgress { 0% { width: 0%; } 100% { width: 100%; } }
        .animate-toastProgress { animation: toastProgress 4s linear forwards; }
      `}</style>
      {/* BREADCRUMBS */}
      <nav className="mb-2 sm:mb-3 flex-shrink-0 px-0.5 sm:px-0">
        <ol className="flex items-center whitespace-nowrap">
          <li className="inline-flex items-center">
            <Link className="flex items-center text-xs sm:text-sm text-theme-subtext hover:text-theme-brand transition-colors" to="/dashboard">Dashboard</Link>
            <svg className="mx-1.5 sm:mx-2 w-3.5 h-3.5 text-theme-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
          </li>
          <li className="text-xs sm:text-sm font-semibold text-theme-text truncate">Payments</li>
        </ol>
      </nav>

     {/* HEADER & DESKTOP ACTIONS */}
     <div className="flex items-center justify-between mb-2.5 sm:mb-4 flex-shrink-0 px-0.5 sm:px-0">
        <h2 className="text-lg sm:text-2xl font-extrabold text-theme-text tracking-tight truncate mr-2">Payment History</h2>
        
        <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
          <button onClick={handleRefresh} className="p-2 rounded-md sm:rounded-lg border border-theme-border bg-theme-surface text-theme-subtext hover:text-theme-text transition-colors shadow-sm focus:outline-none" title="Refresh">
            <svg className={`w-4 h-4 sm:w-5 sm:h-5 ${isLoading ? 'animate-spin text-theme-brand' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>
          </button>
          
          {/* 🌟 FIXED EXPORT BUTTON (Visible on Mobile) */}
          <button onClick={handleExport} className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-2 rounded-md sm:rounded-lg border border-theme-border bg-theme-surface text-sm font-bold text-theme-subtext hover:text-theme-text transition-colors shadow-sm focus:outline-none">
            <svg className="w-4 h-4 text-green-600 dark:text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
            <span className="hidden sm:inline">Export Excel</span>
          </button>
          
          <button 
            onClick={handleRecordPaymentClick} 
            disabled={isCheckingPending}
            className="flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 rounded-md sm:rounded-lg bg-theme-brand hover:bg-blue-700 text-white text-sm font-bold shadow-md transition-all active:scale-95 focus:outline-none disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isCheckingPending ? (
              <svg className="animate-spin w-4 h-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4"></path>
              </svg>
            )}
            <span className="hidden sm:inline">Record Payment</span>
            <span className="sm:hidden">New</span>
          </button>
        </div>
      </div>

      {/* FILTERS & SEARCH CARD */}
      <div className="flex flex-col xl:flex-row items-center gap-3 mb-2.5 sm:mb-4 flex-shrink-0 bg-theme-surface p-2.5 sm:p-3 rounded-lg border border-theme-border shadow-sm w-full">
        
        {/* Search & Mobile Filter Toggle */}
        <div className="flex items-center gap-2 w-full xl:w-auto">
          <div className="relative flex-1 xl:w-64">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-theme-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
            <input type="text" placeholder="Search Txn ID, Sender..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-9 pr-3 py-2 rounded-lg border border-theme-border bg-theme-bg text-sm text-theme-text font-medium outline-none focus:border-theme-brand transition-all" />
          </div>
          <button onClick={() => setIsFilterModalOpen(true)} className="xl:hidden flex items-center justify-center gap-2 px-4 py-2 bg-theme-bg border border-theme-border rounded-lg text-sm font-bold text-theme-text transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/></svg>
            Filters {hasActiveFilters && <span className="w-2 h-2 rounded-full bg-red-500"></span>}
          </button>
        </div>
        
        {/* Desktop Inline Filters */}
        <div className="hidden xl:flex flex-wrap items-center gap-2 w-full flex-1">
          <FilterInputs />
          
          <div className="flex items-center gap-3 ml-auto">
            {hasActiveFilters && (
              <button onClick={handleResetFilters} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold border bg-danger-bg border-danger-border text-danger-text hover:bg-red-100 transition-all shadow-sm active:scale-95 whitespace-nowrap">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"/></svg> Reset
              </button>
            )}
            <div className="flex items-center pl-3 border-l border-theme-border">
              <p className="text-sm font-bold text-theme-subtext whitespace-nowrap">
                Total: <span className="text-theme-text ml-1">{filteredPayments.length}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* MOBILE FILTER MODAL */}
     {isFilterModalOpen && createPortal(
        <div className="fixed inset-0 z-[100000] bg-black/60 dark:bg-black/75 backdrop-blur-sm flex items-center justify-center p-4 xl:hidden bg-fade-in">
          
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 flex flex-col w-full max-w-[380px] max-h-[85vh] modal-blow-up">
            
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex-shrink-0">
              <h3 className="font-bold text-lg text-slate-900 dark:text-white flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/></svg>
                Filter Payments
              </h3>
              <button onClick={() => setIsFilterModalOpen(false)} className="p-1.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-md text-slate-500 dark:text-slate-400 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
  
            {/* Body */}
            <div className="p-5 flex flex-col gap-4 overflow-y-auto custom-scrollbar flex-1">
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Status</label>
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={`${F} w-full`}>
                  <option value="all">All Status</option><option value="approved">Approved</option><option value="pending">Pending</option><option value="rejected">Rejected</option>
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Method</label>
                  <select value={filterMethod} onChange={e => setFilterMethod(e.target.value)} className={`${F} w-full`}><option value="all">Any</option><option value="upi">UPI</option><option value="Bank Transfer">Bank</option><option value="cash">Cash</option></select>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Type</label>
                  <select value={filterType} onChange={e => setFilterType(e.target.value)} className={`${F} w-full`}><option value="all">Any</option><option value="registration">Joining</option><option value="installment">Installment</option><option value="full">Full</option></select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Amount</label>
                  <input type="number" min="0" value={filterAmount} onChange={e => setFilterAmount(e.target.value)} placeholder="₹" className={`${F} w-full`} />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Month</label>
                  <input type="number" min="1" max="24" value={filterMonth} onChange={e => setFilterMonth(e.target.value)} placeholder="#" className={`${F} w-full`} />
                </div>
              </div>
              
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Date Range</label>
                <div className="flex flex-col gap-2 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                  <select value={dateType} onChange={e => setDateType(e.target.value)} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md py-1.5 px-3 text-sm font-semibold outline-none text-slate-700 dark:text-slate-300 w-full">
                    <option value="submitted_at">Submitted Date</option><option value="paid_at">Paid Date</option><option value="verified_at">Verified Date</option>
                  </select>
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md py-1.5 px-2 text-xs outline-none text-slate-600 dark:text-slate-300 w-full flex-1" />
                    <span className="text-[10px] font-bold text-slate-400">TO</span>
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md py-1.5 px-2 text-xs outline-none text-slate-600 dark:text-slate-300 w-full flex-1" />
                  </div>
                </div>
              </div>
            </div>
  
            {/* Footer */}
            <div className="p-4 border-t border-slate-100 dark:border-slate-700 flex gap-3 flex-shrink-0 bg-slate-50/50 dark:bg-slate-900/20 rounded-b-xl">
               <button onClick={handleResetFilters} className="flex-1 py-2.5 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-bold rounded-lg text-sm transition-colors hover:bg-slate-100 dark:hover:bg-slate-700">Clear</button>
               <button onClick={() => setIsFilterModalOpen(false)} className="flex-[2] py-2.5 bg-indigo-600 text-white font-bold rounded-lg text-sm transition-colors hover:bg-indigo-700 shadow-sm">Apply Filters</button>
            </div>
            
          </div>
        </div>,
        document.body
      )}
      {/* TABLE */}
      <div className="flex-1 min-h-0 bg-theme-surface rounded-lg border border-theme-border shadow-sm overflow-hidden flex flex-col relative w-full">
        <div className="h-full w-full overflow-auto custom-scrollbar">
        <table className="w-full text-left border-collapse whitespace-nowrap min-w-max">
            <thead className="sticky top-0 z-10 bg-slate-500 dark:bg-slate-900 shadow-md ring-1 ring-slate-900">
              <tr>
                <th className="px-3 sm:px-4 py-2.5 sm:py-3.5 text-[11px] sm:text-[13px] font-bold text-white uppercase text-center w-12">Sr. No</th>
                <th className="px-3 sm:px-4 py-2.5 sm:py-3.5 text-[11px] sm:text-[13px] font-bold text-white uppercase text-center w-16">View</th>
                <th className="px-3 sm:px-4 py-2.5 sm:py-3.5 text-[11px] sm:text-[13px] font-bold text-white uppercase">Payment ID</th>
                <th className="px-3 sm:px-4 py-2.5 sm:py-3.5 text-[11px] sm:text-[13px] font-bold text-white uppercase">Amount</th>
                <th className="px-3 sm:px-4 py-2.5 sm:py-3.5 text-[11px] sm:text-[13px] font-bold text-white uppercase">Transaction ID</th>
                <th className="px-3 sm:px-4 py-2.5 sm:py-3.5 text-[11px] sm:text-[13px] font-bold text-white uppercase">Sender</th>
                <th className="px-3 sm:px-4 py-2.5 sm:py-3.5 text-[11px] sm:text-[13px] font-bold text-white uppercase">Method</th>
                <th className="px-3 sm:px-4 py-2.5 sm:py-3.5 text-[11px] sm:text-[13px] font-bold text-white uppercase">Type</th>
                <th className="px-3 sm:px-4 py-2.5 sm:py-3.5 text-[11px] sm:text-[13px] font-bold text-white uppercase text-center">Month</th>
                <th className="px-3 sm:px-4 py-2.5 sm:py-3.5 text-[11px] sm:text-[13px] font-bold text-white uppercase">Status</th>
                <th className="px-3 sm:px-4 py-2.5 sm:py-3.5 text-[11px] sm:text-[13px] font-bold text-white uppercase">Remarks</th>
                <th className="px-3 sm:px-4 py-2.5 sm:py-3.5 text-[11px] sm:text-[13px] font-bold text-white uppercase">Submitted At</th>
                <th className="px-3 sm:px-4 py-2.5 sm:py-3.5 text-[11px] sm:text-[13px] font-bold text-white uppercase">Verified At</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-theme-border bg-theme-surface">
              {isLoading ? (
                <tr>
                  <td colSpan="14" className="px-5 py-12 text-center text-sm font-medium text-theme-muted bg-theme-surface">
                    <svg className="w-8 h-8 mx-auto text-theme-brand animate-spin mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>
                    Loading payments...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="14" className="px-5 py-12 text-center text-sm font-medium text-danger-text bg-theme-surface">
                    <p>{error}</p>
                    <button onClick={fetchPayments} className="mt-2 text-theme-brand underline font-bold">Try Again</button>
                  </td>
                </tr>
              ) : filteredPayments.length === 0 ? (
                <tr>
                  <td colSpan="14" className="px-5 py-12 text-center text-sm font-medium text-theme-muted bg-theme-surface">
                    <svg className="w-10 h-10 mx-auto text-theme-border mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                    No payments found matching your filters.
                  </td>
                </tr>
              ) : (
                filteredPayments.map((payment, index) => (
                  <tr key={payment.id} className="hover:bg-theme-bg/80 transition-colors group">
                    
                    <td className="px-3 sm:px-4 py-2.5 sm:py-3.5 text-center">
                      <p className="text-xs sm:text-sm font-bold text-slate-500">{index + 1}</p>
                    </td>

                    <td className="px-3 sm:px-4 py-2.5 sm:py-3.5 text-center">
                      <button onClick={() => handleViewProof(payment.id)} className="inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-md bg-theme-bg border border-theme-border text-theme-muted hover:bg-theme-brand hover:text-white hover:border-theme-brand transition-colors shadow-sm focus:outline-none" title="View Proof Document">
                        <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path></svg>
                      </button>
                    </td>

                    <td className="px-3 sm:px-4 py-2.5 sm:py-3.5"><p className="text-xs sm:text-sm font-medium text-theme-text">{payment.id}</p></td>
                    <td className="px-3 sm:px-4 py-2.5 sm:py-3.5">
                      <p className="text-xs sm:text-sm font-extrabold text-success-text bg-success-bg border border-success-border px-2 py-1 rounded w-fit shadow-sm">
                        ₹ {payment.amount}
                      </p>
                    </td>
                    <td className="px-3 sm:px-4 py-2.5 sm:py-3.5">
                      <p className="text-xs sm:text-sm font-bold text-theme-brand font-mono bg-theme-brandLight border border-theme-border px-2 py-1 rounded w-fit shadow-sm">
                        {payment.transaction}
                      </p>
                    </td>
                    <td className="px-3 sm:px-4 py-2.5 sm:py-3.5">
                      <div className="flex items-center gap-2">
                        <div className="hidden md:flex w-6 h-6 rounded-md bg-theme-bg border border-theme-border items-center justify-center text-theme-subtext font-bold text-[10px] uppercase">
                          {payment.sender ? payment.sender.charAt(0) : '?'}
                        </div>
                        <p className="text-xs sm:text-sm font-medium text-theme-text">{payment.sender}</p>
                      </div>
                    </td>
                    <td className="px-3 sm:px-4 py-2.5 sm:py-3.5"><p className="text-xs sm:text-sm font-medium text-theme-text">{payment.method}</p></td>
                    <td className="px-3 sm:px-4 py-2.5 sm:py-3.5"><p className="text-xs sm:text-sm font-medium text-theme-text capitalize">{payment.type}</p></td>
                    <td className="px-3 sm:px-4 py-2.5 sm:py-3.5 text-center"><p className="text-xs sm:text-sm font-medium text-theme-text">{payment.month || "-"}</p></td>
                    <td className="px-3 sm:px-4 py-2.5 sm:py-3.5">{getStatusBadge(payment.status)}</td>
                    <td className="px-3 sm:px-4 py-2.5 sm:py-3.5 max-w-[140px] truncate" title={payment.remarks}>
                      <p className="text-xs sm:text-sm font-medium text-theme-text truncate">{payment.remarks || "-"}</p>
                    </td>
                    <td className="px-3 sm:px-4 py-2.5 sm:py-3.5"><p className="text-xs sm:text-sm font-medium text-theme-text">{formatDate(payment.submitted_at)}</p></td>
                    <td className="px-3 sm:px-4 py-2.5 sm:py-3.5"><p className="text-xs sm:text-sm font-medium text-theme-text">{formatDate(payment.verified_at)}</p></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* SUCCESS DOWNLOAD TOAST */}
      {downloadToast.show && (
        <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[1000] animate-slideInRight w-[calc(100vw-2rem)] sm:w-auto max-w-[280px] sm:max-w-[320px]">
          <div className="relative flex items-center gap-3 px-3.5 py-4 bg-green-600 dark:bg-green-700 text-white rounded-lg shadow-2xl overflow-hidden border border-green-500 dark:border-green-600">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-md bg-white/20 flex items-center justify-center shrink-0">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/></svg>
            </div>
            <div className="flex flex-col pr-6">
              <span className="text-[13px] sm:text-sm font-extrabold tracking-tight">Export Complete</span>
              <span className="text-[10px] sm:text-xs text-green-100 font-medium truncate max-w-[170px] sm:max-w-[200px]">
                {downloadToast.filename}
              </span>
            </div>
            <button 
              onClick={() => setDownloadToast({ show: false, filename: '' })} 
              className="absolute top-1/2 -translate-y-1/2 right-2.5 p-1.5 text-green-200 hover:text-white hover:bg-white/20 rounded-md transition-colors focus:outline-none"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
            <div className="absolute bottom-0 left-0 h-1.5 bg-white shadow-[0_-2px_8px_rgba(255,255,255,0.9)] animate-toastProgress rounded-r-full"></div>
          </div>
        </div>
      )}

      {/* CREATE PAYMENT MODAL */}
{isCreateModalOpen && (
  <div className="fixed inset-0 bg-[#0A1128]/80 backdrop-blur-sm z-[100] flex items-center justify-center p-2 sm:p-4 md:p-6">
    {/* Main Modal Container */}
    <div className="bg-white dark:bg-[#1e293b] w-full max-w-3xl max-h-[95vh] sm:max-h-[90vh] rounded-xl shadow-2xl dark:shadow-[0_0_20px_rgba(0,0,0,0.5)] flex flex-col overflow-hidden animate-blowUp border border-transparent dark:border-white/10">
      
      {/* Header */}
      <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-200 dark:border-white/10 flex items-center justify-between bg-slate-50 dark:bg-slate-800/50 flex-shrink-0">
        <div>
          <h3 className="text-base sm:text-lg md:text-xl font-extrabold text-slate-900 dark:text-white tracking-tight">Record Payment</h3>
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mt-0.5">Submit transaction details and proof for verification.</p>
        </div>
        <button onClick={handleCloseCreateModal} className="text-slate-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 transition-colors p-1 sm:p-1.5 bg-white dark:bg-slate-800 rounded-full border border-slate-200 dark:border-slate-600 shadow-sm focus:outline-none">
          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
        </button>
      </div>

      {/* Body */}
      <div className="p-4 sm:p-6 overflow-y-auto custom-scrollbar flex-1 bg-white dark:bg-[#1e293b]">
        <form id="paymentForm" onSubmit={handleSubmitPayment} className="space-y-4 sm:space-y-5">
          
          {/* 1. TYPE AND METHOD ROW */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">Payment Type *</label>
              <select name="type" value={paymentForm.type} onChange={handleFormChange} className="w-full px-3 py-2.5 rounded-md border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-200 font-bold outline-none focus:bg-white dark:focus:bg-slate-700 focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition-all cursor-pointer">
                <option value="installment">Installment</option>
                <option value="registration">Registration</option>
                <option value="full">Full Payment</option>
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">Payment Method *</label>
              <select name="method" value={paymentForm.method} onChange={handleFormChange} className="w-full px-3 py-2.5 rounded-md border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-200 font-bold outline-none focus:bg-white dark:focus:bg-slate-700 focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition-all cursor-pointer">
                <option value="UPI">UPI</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Cash">Cash</option>
              </select>
            </div>
          </div>

          {/* 2. DYNAMIC INSTALLMENT SELECTOR */}
          {paymentForm.type === 'installment' && (
            <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-3 sm:p-4">

              {/* ── Header: checkbox + count badge ── */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <input
                    id="select-all-installments"
                    type="checkbox"
                    disabled={isLoadingInstallments || remainingInstallments.length === 0}
                    checked={remainingInstallments.length > 0 && selectedInstallments.length === remainingInstallments.length}
                    ref={el => {
                      if (el) el.indeterminate = selectedInstallments.length > 0 && selectedInstallments.length < remainingInstallments.length;
                    }}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedInstallments([...remainingInstallments].sort((a, b) => a.no - b.no).map(i => i.no));
                      } else {
                        setSelectedInstallments([]);
                      }
                    }}
                    className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 accent-blue-600 cursor-pointer disabled:cursor-not-allowed"
                  />
                  <label htmlFor="select-all-installments" className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer select-none">
                    Select All Remaining
                  </label>
                </div>

                {/* Selection count + calculated total badge */}
                {selectedInstallments.length > 0 ? (
                  <span className="text-[11px] font-bold text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 px-2.5 py-1 rounded-md border border-blue-200 dark:border-blue-700 whitespace-nowrap">
                    {selectedInstallments.length} of {remainingInstallments.length} month{selectedInstallments.length > 1 ? 's' : ''} · ₹{paymentForm.amount || 0}
                  </span>
                ) : (
                  <span className="text-[11px] font-medium text-slate-400 dark:text-slate-500 whitespace-nowrap">
                    0 of {remainingInstallments.length} selected
                  </span>
                )}
              </div>
              
              {isLoadingInstallments ? (
                <div className="flex justify-center items-center py-6">
                  <svg className="animate-spin h-6 w-6 text-blue-600" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                </div>
              ) : remainingInstallments.length === 0 ? (
                <p className="text-sm font-bold text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400 text-center py-4 rounded-md border border-green-100 dark:border-green-800/50">All installments paid! 🎉</p>
              ) : (
                (() => {
                  const overdueNos = remainingInstallments.filter(i => i.status === 'overdue').map(i => i.no);
                  const allOverduesSelected = overdueNos.every(no => selectedInstallments.includes(no));

                  return (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 max-h-[160px] overflow-y-auto custom-scrollbar pr-1">
                      {remainingInstallments.map((inst) => {
                        const isSelected = selectedInstallments.includes(inst.no);
                        const isOverdue = inst.status === 'overdue';
                        const isLocked = !isOverdue && !allOverduesSelected && !isSelected;
                        
                        return (
                          <div 
                            key={inst.no} 
                            onClick={() => !isLocked && handleToggleInstallment(inst)}
                            className={`border rounded-md p-2.5 transition-all flex flex-col items-center justify-center text-center select-none shadow-sm
                              ${isLocked 
                                ? 'opacity-40 cursor-not-allowed bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700' 
                                : isSelected 
                                  ? 'cursor-pointer border-blue-600 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-600 hover:-translate-y-0.5' 
                                  : isOverdue 
                                    ? 'cursor-pointer border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/30 hover:-translate-y-0.5' 
                                    : 'cursor-pointer border-slate-200 bg-white dark:bg-slate-800 dark:border-slate-700 hover:border-blue-300 hover:-translate-y-0.5'}`}
                          >
                            <span className={`text-xs font-extrabold mb-1 ${isSelected ? 'text-blue-700 dark:text-blue-400' : 'text-slate-700 dark:text-slate-300'}`}>
                              Month {inst.no}
                            </span>
                            
                            <p className={`text-[11px] font-bold ${isOverdue ? 'text-red-700 dark:text-red-400' : 'text-slate-500 dark:text-slate-400'}`}>
                              ₹{inst.total}
                            </p>
                            
                            {isOverdue && (
                              <span className="text-[9px] font-bold text-white bg-red-500 px-1.5 py-0.5 rounded-sm mt-1.5 tracking-wider">
                                OVERDUE
                              </span>
                            )}
                            
                            {isLocked && (
                              <span className="text-[9px] font-bold text-slate-400 mt-1.5 tracking-wider">
                                LOCKED
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()
              )}
            </div>
          )}

          {/* 3. AMOUNT AND TRANSACTION ID ROW */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">
                {paymentForm.type === 'installment' ? 'Calculated Amount (₹) *' : 'Amount (₹) *'}
              </label>
              <input 
                required 
                type="number"
                min="1"
                step="0.01"
                name="amount" 
                value={paymentForm.amount} 
                onChange={handleFormChange}
                readOnly={paymentForm.type === 'installment'}
                placeholder={paymentForm.type === 'installment' ? "Select months above" : "e.g. 1000"} 
                className={`w-full px-3 py-2.5 rounded-md border text-sm font-bold outline-none transition-all
                  ${paymentForm.type === 'installment' 
                    ? 'border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 cursor-not-allowed select-none' 
                    : 'border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white focus:bg-white dark:focus:bg-slate-700 focus:border-blue-600 focus:ring-1 focus:ring-blue-600'
                  }`} 
              />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">Transaction ID / UTR *</label>
              <input required type="text" minLength="5" pattern="[a-zA-Z0-9]+" title="Letters and numbers only, minimum 5 characters." name="transaction" value={paymentForm.transaction} onChange={handleFormChange} placeholder="Enter unique transaction number" className="w-full px-3 py-2.5 rounded-md border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-sm text-slate-900 dark:text-white font-mono font-bold outline-none focus:bg-white dark:focus:bg-slate-700 focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition-all" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">Sender Name *</label>
              <input required type="text" minLength="2" pattern="[a-zA-Z\s]+" title="Letters and spaces only." name="sender" value={paymentForm.sender} onChange={handleFormChange} placeholder="As per bank record" className="w-full px-3 py-2.5 rounded-md border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-sm text-slate-900 dark:text-white font-bold outline-none focus:bg-white dark:focus:bg-slate-700 focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition-all" />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">Date of Payment *</label>
              <input required type="datetime-local" name="paid_at" min={minDateString} max={maxDateString} value={paymentForm.paid_at} onChange={handleFormChange} className="w-full px-3 py-2.5 rounded-md border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-sm text-slate-900 dark:text-white font-bold outline-none focus:bg-white dark:focus:bg-slate-700 focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition-all cursor-pointer" />
            </div>
          </div>

          <hr className="border-slate-200 dark:border-slate-700 my-2" />

          <div>
            <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">Proof Screenshot *</label>
            {!previewUrl ? (
              <div className="w-full flex flex-col items-center justify-center p-5 sm:p-8 border-2 border-slate-300 dark:border-slate-600 border-dashed rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors cursor-pointer bg-[#F8FAFC] dark:bg-slate-800/30 group">
                <svg className="h-9 w-9 text-slate-400 dark:text-slate-500 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors mb-2.5" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                  <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <label htmlFor="file-upload" className="cursor-pointer text-sm font-bold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 px-4 py-2 rounded bg-blue-50 dark:bg-blue-500/10 transition-colors">
                  Browse Files
                  <input id="file-upload" type="file" accept="image/png, image/jpeg, image/jpg" className="sr-only" onChange={handleFileChange} ref={fileInputRef} />
                </label>
                <p className="text-[11px] font-medium text-slate-400 dark:text-slate-500 mt-2.5 text-center">Max 2MB. Image will be automatically compressed.</p>
              </div>
            ) : (
              <div className="w-full flex flex-col sm:flex-row items-center gap-3 border border-slate-200 dark:border-slate-700 rounded-lg p-3 bg-slate-50 dark:bg-slate-800/50 shadow-sm">
                <div className="w-full sm:w-1/3 flex items-center justify-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded p-2">
                  <img src={previewUrl} alt="Preview" className="max-h-[100px] w-auto object-contain rounded" />
                </div>
                <div className="flex-1 flex flex-col justify-center text-center sm:text-left">
                  <p className="text-sm font-bold text-slate-700 dark:text-slate-200 truncate">{selectedFile.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  <button type="button" onClick={clearFile} className="mt-2.5 text-xs font-bold text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 px-3 py-1.5 rounded transition-colors w-fit mx-auto sm:mx-0">
                    Remove Image
                  </button>
                </div>
              </div>
            )}
          </div>
        </form>
      </div>
      
      {/* Footer */}
      <div className="px-4 sm:px-6 py-3 border-t border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-2 flex-shrink-0">
        <button type="button" onClick={handleCloseCreateModal} className="px-4 py-2 rounded-md text-sm font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">Cancel</button>
        <button form="paymentForm" type="submit" disabled={isSubmitting} className="flex items-center justify-center min-w-[120px] px-5 py-2 rounded-md bg-blue-600 text-white text-sm font-bold shadow-md hover:bg-blue-700 transition-all active:scale-95 disabled:opacity-70">
          {isSubmitting ? (
            <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
          ) : "Submit Data"}
        </button>
      </div>
    </div>
  </div>
)}

      {/* VIEW PROOF MODAL */}
      {viewProofUrl && (
        <div 
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-md z-[120] flex items-center justify-center p-4 md:p-8 animate-in fade-in duration-200" 
          onClick={closeProofModal}
        >
          <div className="bg-white rounded-lg shadow-2xl animate-blowUp max-w-4xl w-full max-h-[calc(100vh-2rem)] md:max-h-[calc(100vh-4rem)] flex flex-col border border-slate-200 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-3 sm:p-4 border-b border-slate-200 bg-slate-50 flex-shrink-0">
              <h3 className="font-bold text-slate-800 text-sm sm:text-base tracking-tight">Payment Proof Document</h3>
              <button onClick={closeProofModal} className="text-slate-500 hover:text-red-500 hover:bg-red-50 p-1.5 rounded-md transition-colors border border-transparent hover:border-red-100 focus:outline-none">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="square" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>
            <div className="p-3 sm:p-4 overflow-hidden flex items-center justify-center bg-slate-100/50 flex-1">
              <img src={viewProofUrl} alt="Payment Proof" className="max-w-full max-h-full object-contain rounded-md shadow-sm border border-slate-200/50" />
            </div>
          </div>
        </div>
      )}

      {/* CUSTOM ALERT / ERROR MODAL */}
      {alertConfig.isOpen && (
        <div className="fixed inset-0 bg-[#0A1128]/50 backdrop-blur-sm z-[200] flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-sm rounded-xl shadow-2xl flex flex-col overflow-hidden animate-blowUp text-center">
            <div className="p-6 pt-8 flex flex-col items-center">
              {alertConfig.type === 'error' ? (
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4 ring-8 ring-red-50">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth="2.5" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                </div>
              ) : (
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4 ring-8 ring-green-50">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                </div>
              )}
              <h3 className="text-xl font-extrabold text-slate-900 mb-2">{alertConfig.title}</h3>
              <p className="text-sm font-medium text-slate-500 leading-relaxed">{alertConfig.message}</p>
            </div>
            <div className="p-4 bg-slate-50 border-t border-slate-100">
              <button 
                onClick={closeAlert} 
                className={`w-full py-2.5 rounded-lg text-sm font-bold text-white shadow-md transition-all active:scale-95 ${alertConfig.type === 'error' ? 'bg-red-600 hover:bg-red-700 shadow-red-600/20' : 'bg-green-600 hover:bg-green-700 shadow-green-600/20'}`}
              >
                Okay, got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending Payment Warning Modal */}
    {showPendingAlert && (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-sm p-6 text-center animate-in zoom-in-95 slide-in-from-bottom-4 duration-300">
          
          <div className="w-16 h-16 bg-amber-100 dark:bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-inner">
            <svg className="w-8 h-8 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          
          <h3 className="text-xl font-black text-slate-900 dark:text-white mb-2">Payment Under Review</h3>
          
          <p className="text-sm text-slate-500 dark:text-slate-400 font-medium mb-6 leading-relaxed">
            Your previous payment is currently pending admin approval. You cannot record a new payment until the pending one is verified.
          </p>
          
          <button 
            onClick={() => setShowPendingAlert(false)}
            className="w-full bg-slate-900 dark:bg-indigo-600 hover:bg-slate-800 dark:hover:bg-indigo-700 text-white text-sm font-bold py-3 rounded-xl transition-all active:scale-[0.98]"
          >
            Understood
          </button>
          
        </div>
      </div>
    )}

    </div>
  );
};

export default Payments;